import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import nookies from 'nookies';
import Content2Up from 'components/Content2Up';
import Carousel from 'components/LandingPage/Carousel';
import Image4UpBlock from 'components/LandingPage/Image4UpBlock';
import PageMeta from 'components/PageMeta';
import StorePageBannerDescription from 'components/StorePageBannerDescription';
import StorePageInfo from 'components/StorePageInfo';
import { DEFAULT_LOCATION } from 'config/serverConfig';
import getCachedStoreInformation from 'data/cache/get-store-information';
import getContentData from 'data/contentstack/get-content-data';
import { getNewsletterEmailCaptureData } from 'data/contentstack/get-newsletter-emailcapture-content';
import universalHydration from 'data/universal-hydration';
import sspMiddleware from 'middleware/gssp';
import { setStoreSelectionByName } from 'stores/actions/store-selection';
import { showCartWarningModal } from 'stores/reducers/cart-warning-modal';
import setCaching from 'utils/cache-control';
import formatStoreName from 'utils/format-store-name';
import getCanonicalUrl from 'utils/get-canonical-url';
import getCorrelationId from 'utils/get-correlation-id';
import isEmpty from 'utils/is-empty';
import { sendPageView, setMetricsData } from 'utils/metrics';
import styles from './stores.module.scss';

export default function StorePage({
  canonical,
  location,
  menu,
  retailerInfo: { dutchie_hours: hours = [] },
  retailerUrlMap,
  storePage: {
    store_name: storeName,
    carousel,
    four_up_content_block: fourUpContentBlock,
    banner_description: bannerDescription,
    store_information: storeInformation,
    cultivated_products: cultivatedProducts,
    curated_products: curatedProducts,
    gold_label_products: goldLabelProducts,
    recreational_products: recreationalProducts,
    medical_products: medicalProducts,
    effects,
    seo_store_page: {
      meta_title: title,
      meta_description: description,
      keywords,
    },
  } = {},
} = {}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const storeSelection = useSelector((state) => state.storeSelection);
  const cartInitialized = useSelector((state) => !isEmpty(state.cart.scope));
  const hasLineItems = useSelector((state) => state.cart.lineItems.length > 0);
  const initialStoreSelection = useRef(storeSelection);

  const carouselProps =
    carousel && Array.isArray(carousel) && carousel.length > 0
      ? carousel[0]
      : null;

  const fourUpContentBlockProps =
    fourUpContentBlock &&
    Array.isArray(fourUpContentBlock) &&
    fourUpContentBlock.length > 0
      ? fourUpContentBlock[0]
      : null;

  useEffect(() => {
    setMetricsData({
      location,
      menu,
      pageType: 'store_page',
      title,
    });
    sendPageView(title);
  }, [location, menu, title]);

  useEffect(() => {
    const formattedStoreSelection = formatStoreName(storeSelection);
    if (
      !isEmpty(storeName) &&
      formatStoreName(storeName) !== formattedStoreSelection &&
      storeSelection === initialStoreSelection.current
    ) {
      if (cartInitialized && hasLineItems) {
        dispatch(
          showCartWarningModal({
            pendingStoreName: storeName,
            type: 'store',
            navigate: true,
            navigateToOnCancel:
              retailerUrlMap[
                formatStoreName(initialStoreSelection?.current ?? '')
              ],
          })
        );
      } else {
        dispatch(setStoreSelectionByName(storeName));
      }
    }

    if (
      storeSelection !== initialStoreSelection.current &&
      retailerUrlMap?.[formattedStoreSelection]
    ) {
      initialStoreSelection.current = storeSelection;
      router.push(retailerUrlMap[formattedStoreSelection]);
    }
  }, [
    dispatch,
    router,
    storeName,
    storeSelection,
    cartInitialized,
    hasLineItems,
    retailerUrlMap,
  ]);

  return (
    <>
      <PageMeta
        canonical={canonical}
        description={description}
        keywords={keywords}
        title={title}
      />
      <div>
        {carouselProps && <Carousel {...carouselProps} />}
        {fourUpContentBlockProps && (
          <Image4UpBlock {...fourUpContentBlockProps} />
        )}
        <StorePageBannerDescription {...bannerDescription} />
        <StorePageInfo
          storeHours={hours}
          priority={!carouselProps && !fourUpContentBlockProps}
          {...storeInformation}
        />
        {[
          cultivatedProducts,
          curatedProducts,
          goldLabelProducts,
          recreationalProducts,
          medicalProducts,
          effects,
        ].map((data = {}) => {
          if (data.hide_section === true) return null;

          const { header, body, image, image_position: position } = data;

          return (
            <div key={header} className="d-flex justify-content-center w-100">
              <div className={styles.mw_80}>
                <Content2Up
                  isSetDangerously
                  customHeadingClassName="text-primary fs-12 fs-xl-18"
                  customPaddingClassName="px-xl-0 py-20 px-10"
                  isRowReverse={position === 'right'}
                  bodyText={body}
                  heading={header}
                  hero={{
                    title: image?.file?.alt_text,
                    url: image?.file?.url,
                    link: image?.link?.replaceAll('.', ''),
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export const getServerSideProps = sspMiddleware((store) => async (ctx) => {
  setCaching('long', ctx.res);
  const { slug: storeSlug } = ctx.params;
  const { log } = ctx.req;
  const correlationId = getCorrelationId({ request: ctx.req });

  const { store: location = DEFAULT_LOCATION, menu = 'recreational' } =
    nookies.get(ctx);

  const [universal, storePage, cmsEmail, retailersInfo] =
    await Promise.allSettled([
      universalHydration({ ctx, store, correlationId, log }),
      getContentData({
        correlationId,
        id: 'lume_store_pages_v2',
        log,
        params: {
          // eslint-disable-next-line camelcase
          include_branch: true,
          include: ['carousel.items', 'four_up_content_block.items'],
          query: { url: `/${storeSlug}` },
        },
      }),
      getNewsletterEmailCaptureData({ correlationId, log }),
      getCachedStoreInformation({ correlationId, log }),
    ]);

  const path = `/stores/${storeSlug}`;

  const props = {
    canonical: getCanonicalUrl(path),
    location,
    menu,
  };

  if (universal.status === 'fulfilled') {
    props.footer = universal.value.footer;
    props.header = universal.value.header;
  }

  if (storePage.status === 'fulfilled') {
    props.storePage = storePage.value?.data?.[0] ?? {};
    props.jsonld = props.storePage.json_ld || '';
  }

  if (isEmpty(props.storePage)) {
    return {
      notFound: true,
    };
  }

  if (cmsEmail.status === 'fulfilled') {
    props.cmsEmail = cmsEmail.value?.data?.[0] ?? null;
  }

  if (retailersInfo.status === 'fulfilled') {
    props.retailerUrlMap = Object.fromEntries(
      retailersInfo.value?.map((ri) => [ri.formattedStoreName, ri.url])
    );
    props.retailerInfo = retailersInfo.value?.find(
      (r) => r.formattedStoreName === formatStoreName(location)
    );

    if (!props.retailerInfo) {
      log.warn(`No retailer info found for ${location}`);
    }
  }

  if (isEmpty(props.storePage.store_name)) {
    return {
      notFound: true,
    };
  }

  props.metrics = {
    title: props.storePage?.seo_store_page?.meta_title,
    pageType: 'store_page',
    location,
    menu,
  };

  return {
    props,
  };
});
